import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import {
  BLOCAJE_ALE_CLIENTULUI, MOTIVE_BLOCARE, SEVERITATI, STARI_RESTANTA,
  SURSE_RESTANTA, eticheta,
} from "./enums.js";
import { data } from "./text.js";
import { tr } from "./i18n.js";

const GESTIUNE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR"];
const ANULARE = ["ADMIN_TENANT", "MANAGER_TEHNIC"];
const eClient = (rol) => String(rol).startsWith("CLIENT_");


/** Vechimea se citește ca un om, nu ca un număr de zile. */
function vechime(zile) {
  if (zile === 0) return { text: "azi", clasa: "" };
  if (zile < 30) return { text: `${zile} ${zile === 1 ? "zi" : "zile"}`, clasa: "" };
  if (zile < 60) return { text: tr("{0} de zile", zile), clasa: "vechime-30" };
  if (zile < 90) return { text: tr("{0} de zile", zile), clasa: "vechime-60" };
  return { text: tr("{0} de zile", zile), clasa: "vechime-90" };
}

export default function Restante({ eu }) {
  const [lista, setLista] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [clientId, setClientId] = useState("");
  const [motiv, setMotiv] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(false);
  const [raport, setRaport] = useState(null);
  const [deschisa, setDeschisa] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");

  const poateGestiona = GESTIUNE.includes(eu.rol);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try {
      setLista(await api.restante({
        clientId: clientId || undefined,
        motiv: motiv || undefined,
        status: status || undefined,
      }));
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [clientId, motiv, status]);

  useEffect(() => { reincarca(); }, [reincarca]);
  useEffect(() => { if (!eClient(eu.rol)) api.clienti().then(setClienti).catch(() => {}); }, [eu.rol]);

  return (
    <div className="continut">
      <h1>{tr("Backlog tehnic")}</h1>
      <p className="subtitlu">
        {tr("Constatările nerezolvate — aștept piesa, aștept aprobarea, n-am avut acces. Se deschid singure din checklist-uri și din fișele de intervenție, și nu se pot pierde: o restanță se rezolvă printr-o intervenție sau se anulează cu motiv scris, dar nu dispare.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-corp">
          <div className="linie-actiune" style={{ alignItems: "center" }}>
            {!eClient(eu.rol) && (
              <label className="camp">{tr("Client")}
                <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">{tr("toți clienții")}</option>
                  {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
                </select>
              </label>
            )}
            <label className="camp">{tr("Cauza blocajului")}
              <select value={motiv} onChange={(e) => setMotiv(e.target.value)}>
                <option value="">{tr("toate cauzele")}</option>
                {Object.entries(MOTIVE_BLOCARE).map(([v, e]) => (
                  <option key={v} value={v}>{tr(e)}</option>
                ))}
              </select>
            </label>
            <label className="camp">{tr("Stare")}
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{tr("în lucru")}</option>
                {Object.entries(STARI_RESTANTA).map(([v, e]) => (
                  <option key={v} value={v}>{tr(e)}</option>
                ))}
              </select>
            </label>
            {poateGestiona && (
              <>
                <button className="buton buton-actiune" onClick={() => setForm(true)}>
                  {tr("restanță nouă")}
                </button>
                <button className="buton buton-mic buton-discret"
                  onClick={async () => {
                    try { setRaport(await api.raportPeCauza(12)); }
                    catch (e) { setEroare(mesajEroare(e)); }
                  }}>
                  {tr("unde se blochează firma")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {raport && <RaportCauze raport={raport} laInchidere={() => setRaport(null)} />}

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : lista.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">
          {status || motiv || clientId
            ? tr("Nicio restanță cu filtrele alese.")
            : tr("Nicio restanță deschisă. Tot ce s-a constatat a fost rezolvat.")}
        </div></div>
      ) : (
        <div className="panou">
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Ce e de făcut")}</th><th>{tr("Client / locație")}</th><th>{tr("Cauza")}</th>
                <th>{tr("Severitate")}</th><th>{tr("Vechime")}</th><th className="num">{tr("Scor")}</th><th />
              </tr>
            </thead>
            <tbody>
              {lista.map((r) => {
                const v = vechime(r.vechimeZile);
                return (
                  <tr key={r.id}>
                    <td>
                      {tr(r.descriere)}
                      <span className="fara-pret">
                        {eticheta(SURSE_RESTANTA, r.sursa)}
                        {r.sistemInstalat ? ` · ${r.sistemInstalat.denumire}` : ""}
                        {r.echipament ? ` · ${r.echipament.eticheta}` : ""}
                      </span>
                    </td>
                    <td>
                      {tr(r.client?.denumire)}
                      <span className="fara-pret">{tr(r.locatie?.denumire)}</span>
                    </td>
                    <td>
                      {eticheta(MOTIVE_BLOCARE, r.motivBlocare)}
                      {r.vizibilaClient && (
                        <span className="marcaj" title={tr("Blocaj de la client — îl vede și el")}>
                          {tr("vizibil clientului")}
                        </span>
                      )}
                    </td>
                    <td>{eticheta(SEVERITATI, r.severitate)}</td>
                    <td><span className={"vechime " + v.clasa}>{v.text}</span></td>
                    <td className="num">{r.scorPrioritate}</td>
                    <td className="celula-actiuni">
                      <button className="buton buton-mic buton-discret"
                        onClick={() => setDeschisa(r)}>{tr("deschide")}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="nota-cautare" style={{ padding: "0 .8rem .8rem" }}>
            {tr("Coada se citește după scor, nu după dată: scorul crește cu vechimea, ca lucrurile mărunte și vechi să nu se sedimenteze la fund pentru totdeauna.")}
          </p>
        </div>
      )}

      {form && (
        <RestantaNoua
          clienti={clienti}
          laInchidere={() => setForm(false)}
          laSalvare={async () => { setForm(false); await reincarca(); }}
        />
      )}

      {deschisa && (
        <Detaliu
          eu={eu} restanta={deschisa}
          laInchidere={() => setDeschisa(null)}
          laSchimbare={async () => { setDeschisa(null); await reincarca(); }}
        />
      )}
    </div>
  );
}

// ── Raportul pe cauză ──────────────────────────────────────────────────────

function RaportCauze({ raport, laInchidere }) {
  const maxim = Math.max(1, ...raport.peCauza.map((c) => c.total));
  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Unde se blochează firma")}</h3>
        <p className="subtitlu">
          {tr("Ultimele")} {raport.perioadaLuni} {tr("luni ·")} {raport.total} {tr("restanțe. Piese, aprobări și acces sunt trei probleme cu trei soluții complet diferite.")}
        </p>

        {raport.total === 0 ? (
          <div className="panou"><div className="panou-corp gol">
            {tr("Nicio restanță în perioada aleasă.")}
          </div></div>
        ) : (
          <div className="panou">
            <table className="tabel tabel-catalog">
              <thead>
                <tr>
                  <th>{tr("Cauza")}</th><th>{tr("Distribuție")}</th><th className="num">{tr("Total")}</th>
                  <th className="num">{tr("Deschise")}</th><th className="num">{tr("Rezolvate")}</th>
                  <th className="num">{tr("Zile medii")}</th>
                </tr>
              </thead>
              <tbody>
                {raport.peCauza.map((c) => (
                  <tr key={c.motiv}>
                    <td>{eticheta(MOTIVE_BLOCARE, c.motiv)}</td>
                    <td>
                      <span className="bara-cauza">
                        <i style={{ width: `${(c.total / maxim) * 100}%` }} />
                      </span>
                    </td>
                    <td className="num">{c.total}</td>
                    <td className="num">{c.deschise}</td>
                    <td className="num">{c.rezolvate}</td>
                    <td className="num">{c.zileMedii ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide")}</button>
        </div>
      </div>
    </div>
  );
}

// ── Restanță nouă ──────────────────────────────────────────────────────────

function RestantaNoua({ clienti, laInchidere, laSalvare }) {
  const [clientId, setClientId] = useState("");
  const [arbore, setArbore] = useState([]);
  const [locatieId, setLocatieId] = useState("");
  const [descriere, setDescriere] = useState("");
  const [motivBlocare, setMotiv] = useState("AsteaptaPiesa");
  const [severitate, setSeveritate] = useState("Important");
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  useEffect(() => {
    if (!clientId) { setArbore([]); return; }
    setLocatieId("");
    api.parc(clientId).then(setArbore).catch((e) => setEroare(mesajEroare(e)));
  }, [clientId]);

  const faraLocatii = !!clientId && arbore.length === 0;
  const alClientului = BLOCAJE_ALE_CLIENTULUI.includes(motivBlocare);

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Restanță nouă")}</h3>
        {eroare && <div className="eroare">{eroare}</div>}

        <div className="formular-linie">
          <label className="camp">{tr("Client")}
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">{tr("— alege —")}</option>
              {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
            </select>
          </label>
          <label className="camp">{tr("Locație")}
            <select value={locatieId} onChange={(e) => setLocatieId(e.target.value)}
              disabled={faraLocatii}>
              <option value="">{faraLocatii ? tr("— clientul n-are nicio locație —") : tr("— alege —")}</option>
              {arbore.map((l) => <option key={l.id} value={l.id}>{tr(l.denumire)}</option>)}
            </select>
          </label>
        </div>

        <label className="camp">{tr("Ce e de făcut")}
          <textarea className="camp-textarea camp-lat" rows={3} value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
            placeholder={tr("Descrierea trebuie să spună ce e de făcut — altfel nu poate fi nici planificată, nici ofertată.")} />
        </label>

        <div className="formular-linie">
          <label className="camp">{tr("De ce stă")}
            <select value={motivBlocare} onChange={(e) => setMotiv(e.target.value)}>
              {Object.entries(MOTIVE_BLOCARE).map(([v, e]) => (
                <option key={v} value={v}>{tr(e)}</option>
              ))}
            </select>
          </label>
          <label className="camp">{tr("Severitate")}
            <select value={severitate} onChange={(e) => setSeveritate(e.target.value)}>
              {Object.entries(SEVERITATI).map(([v, e]) => <option key={v} value={v}>{tr(e)}</option>)}
            </select>
          </label>
        </div>

        <p className="nota-cautare">
          {alClientului
            ? tr("Blocajul e la client, deci îi va fi vizibil — transparență, și o presiune utilă când lucrarea stă din cauza lui.")
            : tr("Blocajul e de partea noastră, deci rămâne în coada internă. Clientul nu-l vede.")}
        </p>

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton buton-actiune"
            disabled={lucreaza || !clientId || !locatieId || descriere.trim().length < 5}
            onClick={async () => {
              setLucreaza(true); setEroare("");
              try {
                await api.creeazaRestanta({
                  clientId, locatieId, descriere: descriere.trim(), motivBlocare, severitate,
                });
                await laSalvare();
              } catch (e) { setEroare(mesajEroare(e)); setLucreaza(false); }
            }}>
            {lucreaza ? tr("se deschide…") : tr("deschide restanța")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detaliu ────────────────────────────────────────────────────────────────

function Detaliu({ eu, restanta, laInchidere, laSchimbare }) {
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");
  const [anulare, setAnulare] = useState(false);
  const [motivAnulare, setMotivAnulare] = useState("");

  const poateGestiona = GESTIUNE.includes(eu.rol);
  const poateAnula = ANULARE.includes(eu.rol);
  const inchisa = restanta.status === "Rezolvata" || restanta.status === "Anulata";
  const v = vechime(restanta.vechimeZile);

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>
          {tr("Restanță")}
          <span className="eticheta" style={{ marginLeft: ".5rem" }}>
            {eticheta(STARI_RESTANTA, restanta.status)}
          </span>
        </h3>
        <p className="subtitlu">
          {tr(restanta.client?.denumire)} · {tr(restanta.locatie?.denumire)} ·{" "}
          {tr("deschisă")} {data(restanta.deschisaLa)}{tr(", acum")} <span className={v.clasa}>{v.text}</span>
        </p>

        {eroare && <div className="eroare">{eroare}</div>}

        <div className="panou">
          <div className="panou-antet">{tr("Ce e de făcut")}</div>
          <div className="panou-corp" style={{ whiteSpace: "pre-wrap" }}>{tr(restanta.descriere)}</div>
        </div>

        <div className="panou">
          <div className="panou-corp">
            <div className="formular-linie">
              <div>
                <div className="fara-pret">{tr("Cauza")}</div>
                <strong>{eticheta(MOTIVE_BLOCARE, restanta.motivBlocare)}</strong>
              </div>
              <div>
                <div className="fara-pret">{tr("Severitate · scor")}</div>
                <strong>{eticheta(SEVERITATI, restanta.severitate)} · {restanta.scorPrioritate}</strong>
              </div>
              <div>
                <div className="fara-pret">{tr("Venită din")}</div>
                <strong>{eticheta(SURSE_RESTANTA, restanta.sursa)}</strong>
              </div>
              <div>
                <div className="fara-pret">{tr("Vizibilă clientului")}</div>
                <strong>{restanta.vizibilaClient ? tr("da") : tr("nu")}</strong>
              </div>
            </div>
            {restanta.anulataMotiv && (
              <p className="nota-cautare">{tr("Anulată:")} {restanta.anulataMotiv}</p>
            )}
          </div>
        </div>

        {!inchisa && poateGestiona && (
          <p className="nota-cautare">
            {tr("O restanță se rezolvă printr-o intervenție care o referă explicit — se face din fișa de intervenție, nu de aici. Aici se poate doar anula, cu motiv scris.")}
          </p>
        )}

        {anulare && (
          <div className="panou">
            <div className="panou-antet">{tr("Motivul anulării")}</div>
            <div className="panou-corp">
              <textarea className="camp-textarea camp-lat" rows={3} value={motivAnulare}
                onChange={(e) => setMotivAnulare(e.target.value)}
                placeholder={tr("Închiderea are întotdeauna o cauză. Minimum 10 caractere.")} />
            </div>
          </div>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide")}</button>
          {!inchisa && poateAnula && (anulare ? (
            <button className="buton buton-sters buton-terminal"
              disabled={lucreaza || motivAnulare.trim().length < 10}
              onClick={async () => {
                setLucreaza(true); setEroare("");
                try {
                  await api.anuleazaRestanta(restanta.id, motivAnulare.trim());
                  await laSchimbare();
                } catch (e) { setEroare(mesajEroare(e)); setLucreaza(false); }
              }}>
              {tr("confirmă anularea")}
            </button>
          ) : (
            <button className="buton buton-sters buton-terminal" onClick={() => setAnulare(true)}>
              {tr("anulează restanța")}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
